import React, { useContext, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { ApplicationFormStateContext } from "../../state/applicationFormState";
import { Loading } from "../../components/Loading";
import { LoadingState } from "../../hooks/loading";
import { ApplicationStatus } from "../../lib-api";
import { Step } from "../../models/Step";
import { trackEvent } from "../../utils/gtm";
import { scrollToTopOfContent } from "../../utils/scroll";
import QuoteBox from "../../components/QuoteBox";
import { useStatusAndQuote } from "../../hooks/statusAndQuote";
import MockCallback from "../../components/MockCallback";


const StatusAndQuoteStep: React.FunctionComponent = () => {
  const [_, dispatch] = useContext(ApplicationFormStateContext);
  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  const query = useQuery();
  const applicationId = query.get("applicationId");

  useEffect(() => {
    trackEvent({eventName: 'step_load', data: { step: 'StatusAndQuote' } });
    dispatch({ type: "set_step", step: Step.StatusAndQuote });
    dispatch({ type: "reset_applicationData" });
    scrollToTopOfContent();
  }, []);

  const { statusAndQuote, loadingState, quoteDecision } = useStatusAndQuote(
    applicationId
  );

  const handleQuoteDecision = async (accept: boolean) => {
    await quoteDecision(statusAndQuote!.applicationId!, accept);
  };

  return (
    <>
      {/* <h2>Status på låneansøgning</h2> */}

      {loadingState === LoadingState.Loading && (
        <div className="mt-4">
          <Loading text="Henter status...." />
        </div>
      )}

      {(loadingState === LoadingState.Error ||
        loadingState === LoadingState.BadRequest ||
        statusAndQuote?.status === ApplicationStatus.TechnicalError ||
        statusAndQuote?.status === ApplicationStatus.None) && (
        <>
          <h2>Fejl!</h2>
          <div className="text-center font-bold text-red-700 m-5">
            Der opstod en fejl. Kontakt support på{" "}
            <a className="font-semibold whitespace-nowrap" href="tel:30140256">
              30 14 02 56
            </a>{" "}
            eller prøv igen.
          </div>
        </>
      )}

      {(loadingState === LoadingState.Success &&
        statusAndQuote?.status === ApplicationStatus.Approved && (
          <>
            <h2>Godkendt</h2>
            {/* <h3 className="mt-3">Godkendt</h3> */}
            <p className="text-center mt-2">
              Resurs Bank A/S har godkendt din låneansøgning.
            </p>
            <p className="text-center mt-2">
              For at få udbetalt lånet skal du acceptere.
            </p>

            <QuoteBox StatusAndQuote={statusAndQuote} />

            <p className="text-center mt-6">
              <button
                type="button"
                className="btn-back"
                onClick={() => handleQuoteDecision(false)}
              >
                Afvis
              </button>
              <button
                type="button"
                className="btn-next"
                onClick={() => handleQuoteDecision(true)}
              >
                Accepter
              </button>
            </p>
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.Rejected && (
          <>
            <h2>Desværre...</h2>
            {/* <h3 className="mt-3">Desværre...</h3> */}
            <p className="text-center mt-2">
              Resurs Bank har afvist din kreditansøgning. Du kan prøve ansøge om
              et lavere beløb, eller søge igen på et senere tidspunkt.
            </p>
            <p className="text-center mt-6">
              <Link to="/">
                <button type="button" className="btn-next">
                  Ansøg igen
                </button>
              </Link>
            </p>
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.ManualInspection && (
          <>
            <h2>En medarbejder skal gennemse din ansøgning</h2>
            {/* <h3 className="mt-3"></h3> */}
            <p className="text-center mt-2">
              Din ansøgning kræver opmærksomhed fra en medarbejder hos Resurs
              Bank. Du vil få besked pr. sms og e-mail indenfor 15 min, hvis du
              har ansøgt indenfor supportens åbningstid 08.00-17.00.
            </p>
            <p className="text-center mt-2">
              Du er også velkommen til at ringe til Resurs Bank på telefon:{" "}
              <a
                className="font-semibold whitespace-nowrap"
                href="tel:39131680"
              >
                39 13 16 80
              </a>{" "}
              eller skrive til <span className="italic">Loan Team</span> på{" "}
              <a className="font-semibold" href="mailto:lt@resurs.dk">
                lt@resurs.dk
              </a>
              .
            </p>
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.WaitingForAddition && (
          <>
            <h2>Ring til os - vi har brug for at vide mere</h2>
            {/* <h3 className="mt-3">Ring til os - vi har brug for at vide mere</h3> */}
            <p className="text-center mt-2">
              Tak for din kreditansøgning hos Resurs Bank A/S. Vi har brug for
              flere oplysninger, og derfor beder vi dig ringe til os. Oftest kan
              det klares på få minutter, hvor du bekræfter dine oplysninger pr.
              telefon, og sommetider bedes du fremsende nogle dokumenter, såsom
              en lønseddel.
            </p>
            <p className="text-center mt-2">
              Kontakt supporten alle ugens dage mellem 08.00-17.00 på{" "}
              <a
                className="font-semibold whitespace-nowrap"
                href="tel:39131680"
              >
                39 13 16 80
              </a>{" "}
              eller <span className="italic">Loan Team</span> på{" "}
              <a className="font-semibold" href="mailto:lt@resurs.dk">
                lt@resurs.dk
              </a>
              .
            </p>
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.CustomerAccepted && (
          <>
            <h2>Du har accepteret lånevilkår</h2>
            {/* <h3 className="mt-3">Du har accepteret lånevilkår</h3> */}
            <p className="text-center mt-2">
              Lånet er under behandling. Du vil blive kontaktet på de angivne
              e-mailadresser (ansøger og medansøger, hvis relevant).
            </p>
            <MoreQuestions />
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.CustomerDeclied && (
          <>
            <h2>Du har afvist lånevilkår</h2>
            <MoreQuestions />
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.PaidOut && (
          <>
            <h2>Dit lån er udbetalt</h2>
            <MoreQuestions />
          </>
        )) ||
        (statusAndQuote?.status === ApplicationStatus.Signed && (
          <>
            <h2>Dit lån er underskrevet</h2>
            <MoreQuestions />
          </>
        ))}
    </>
  );
};

export default StatusAndQuoteStep;

const MoreQuestions: React.FunctionComponent = () => {
  return (
    <p className="text-center mt-2">
      Har du spørgsmål så kontakt supporten alle ugens dage mellem 08.00-17.00
      på{" "}
      <a className="font-semibold whitespace-nowrap" href="tel:39131680">
        39 13 16 80
      </a>{" "}
      eller <span className="italic">Loan Team</span> på{" "}
      <a className="font-semibold" href="mailto:lt@resurs.dk">
        lt@resurs.dk
      </a>
      .
    </p>
  );
};

